import React, { useEffect, useState } from "react";
import { X, Info } from "lucide-react";
import { useBrand } from "../lib/BrandContext";

const DISMISS_KEY = "sf_beta_banner_dismissed_at";
const DISMISS_TTL_DAYS = 14;

/**
 * Small honest banner shown to signed-in coaches during private beta.
 *
 * Copy is intentionally short — we're not asking them to do anything,
 * we're just setting expectations that (a) some features are still
 * shipping and (b) any bug they hit is genuinely useful to us.
 *
 * Dismissible for 14 days per browser. When Resend is wired and the
 * outstanding polish items ship, delete this component entirely.
 */
export default function BetaBanner() {
  const [visible, setVisible] = useState(false);
  const brand = useBrand();

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const ttlMs = DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
    if (!dismissedAt || Date.now() - dismissedAt > ttlMs) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div
      className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
      data-testid="beta-banner"
      role="status"
    >
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          {brand.free_early_access ? "You're using Squad Fee Early Access." : <>You&apos;re on the Squad Fee private beta.</>}
        </p>
        <p className="mt-0.5 text-amber-800">
          Some polish is still landing. Spot a bug or have feedback?{" "}
          <a
            href="mailto:support@squadfee.com?subject=Squad%20Fee%20beta%20feedback"
            className="font-semibold underline decoration-amber-300 underline-offset-2 hover:decoration-amber-900"
            data-testid="beta-banner-email-link"
          >
            support@squadfee.com
          </a>
          .
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded-md p-1 text-amber-700 hover:bg-amber-100"
        aria-label="Dismiss beta notice"
        data-testid="beta-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
